import { Button } from '@/design-system'
import { useLocation } from '@solidjs/router'
import { type Component, Show, onMount } from 'solid-js'
import { I18nProvider, useI18n } from '../../i18n'
import { loadedDictionaries } from '../../i18n/locales'
import { getPreferences } from '../../stores/preferences.store'
import { type ConsentDecision, bannerVisible, initConsent, setConsent } from './consent'

const INTERVIEW_JOURNEY_PATH = /^\/(apply|interview\/(invite|shared)|feedback|f)\//

const ConsentBannerContent: Component = () => {
  const { t } = useI18n()

  const decide = (decision: ConsentDecision) => {
    setConsent(decision)
  }

  return (
    <div
      role="region"
      aria-label={t('consent.banner.ariaLabel')}
      class="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6 pointer-events-none"
    >
      <div class="pointer-events-auto mx-auto w-full max-w-3xl rounded-[var(--radius-lg)] border border-[var(--color-border)] bg-[var(--color-surface)] p-5 shadow-[var(--shadow-lg)] sm:p-6">
        <p class="text-[length:var(--text-base)] font-[var(--font-semibold)] text-[color:var(--color-text)]">
          {t('consent.banner.title')}
        </p>
        <p class="mt-2 text-[length:var(--text-sm)] leading-relaxed text-[color:var(--color-text-muted)]">
          {t('consent.banner.body')}
        </p>

        <div class="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <a
            href="/cookies"
            class="text-[length:var(--text-sm)] font-[var(--font-medium)] text-[color:var(--color-primary-600)] underline underline-offset-2 hover:text-[color:var(--color-primary-700)]"
          >
            {t('consent.banner.policy')}
          </a>

          <div class="flex gap-3">
            <Button
              variant="ghost"
              size="sm"
              class="flex-1 sm:flex-none"
              onClick={() => decide('denied')}
            >
              {t('consent.banner.reject')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              class="flex-1 sm:flex-none"
              onClick={() => decide('granted')}
            >
              {t('consent.banner.accept')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export const ConsentBanner: Component = () => {
  const location = useLocation()

  onMount(() => {
    initConsent()
  })

  const catalogReady = () => {
    const dicts = loadedDictionaries()
    return Boolean(dicts[getPreferences().language] || dicts.en)
  }

  const onInterviewJourney = () => INTERVIEW_JOURNEY_PATH.test(location.pathname)

  return (
    <Show when={bannerVisible() && catalogReady() && !onInterviewJourney()}>
      <I18nProvider>
        <ConsentBannerContent />
      </I18nProvider>
    </Show>
  )
}

export default ConsentBanner
